import { Nami } from '@namiml/web-sdk';

import { getHostURL } from './SDKConfigReader';

/**
 * Fetch initial config over the network for B64-API mode.
 *
 * Mirrors the other test apps' network fetchers (Apple/Android/RN/Roku) and
 * follows the NAM-2482 / PR #474 resilience standard: a 60s timeout with a
 * single retry on timeout only. Non-timeout failures (non-ok HTTP, bad URL)
 * fail immediately with no retry, and there is no infinite loop.
 *
 * @param host The sdkconfig host key (e.g. "staging").
 * @param appPlatformID The app platform ID to fetch config for.
 * @param formFactor The device form factor for the request.
 * @returns The b64_gzip config string, or null on failure.
 */
/**
 * Load a frozen initial config bundled with the app for B64-FILE mode.
 *
 * Reads `public/nami_initial_config.b64` (a fixed name, mirroring the Android
 * test app's asset convention). The file holds the verbatim initial_config API
 * response — the `{"initial_config": "<b64_gzip>"}` JSON wrapper — which
 * `Nami.configure()` decodes the same way as the network path. E2E harnesses
 * (scripts/e2e-maestro.sh) inject the fixture at run time; it is not committed
 * to the app.
 *
 * @returns The initial-config payload string, or null when no fixture is bundled.
 */
export async function loadInitialConfigFromFile(): Promise<string | null> {
  try {
    const response = await fetch('/nami_initial_config.b64', { cache: 'no-store' });
    if (!response.ok) {
      console.log('[getInitialConfig] No bundled initial-config fixture (HTTP', response.status, ')');
      return null;
    }
    const configString = (await response.text()).trim();
    if (!configString) {
      console.log('[getInitialConfig] Bundled initial-config fixture is empty');
      return null;
    }
    console.log('[getInitialConfig] Loaded initial config from bundled fixture');
    return configString;
  } catch (error) {
    console.log('[getInitialConfig] Failed to load bundled initial-config fixture:', error);
    return null;
  }
}

export async function loadInitialConfigFromNetwork(
  host: string,
  appPlatformID: string,
  formFactor: string,
): Promise<string | null> {
  const hostURL = await getHostURL(host);
  if (!hostURL) {
    console.log('[getInitialConfig] No URL resolved for host:', host);
    return null;
  }

  const sdkVersion = Nami.sdkVersion();
  const url = `${hostURL}/sdk/v3/platform/${appPlatformID}/initial_config/?form_factor=${formFactor}&encoding=b64_gzip&sdk_version=${sdkVersion}`;

  // 1 initial attempt + 1 retry on timeout.
  const maxAttempts = 2;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    console.log(
      `[getInitialConfig] Fetching initial config (attempt ${attempt} of ${maxAttempts}) from:`,
      url,
    );

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeout);

      if (!response.ok) {
        console.log('[getInitialConfig] HTTP error:', response.status);
        return null;
      }

      const configString = await response.text();
      console.log(
        '[getInitialConfig] Successfully loaded initial config from network',
      );
      return configString;
    } catch (error) {
      clearTimeout(timeout);
      if ((error as Error).name === 'AbortError') {
        if (attempt < maxAttempts) {
          console.log(
            '[getInitialConfig] Network timeout, retrying initial config',
          );
          continue;
        }
        console.log(
          `[getInitialConfig] Network timeout loading initial config after ${maxAttempts} attempts`,
        );
      } else {
        console.log('[getInitialConfig] Network error:', error);
      }
      return null;
    }
  }

  return null;
}
